import { useRef } from 'react';

import { SectionHeading } from '@/components/home/SectionHeading';
import { useOverflowing } from '@/components/home/useOverflowing';
import { Container } from '@/components/ui/Container';
import { INDUSTRIES, INDUSTRIES_SECTION } from '@/data/homeContent';

/**
 * The ten industry tiles.
 *
 * A tile is a label, not a door: there is no per-industry page to send anyone
 * to, so these are plain list items rather than links that would go nowhere.
 *
 * On a phone the row scrolls sideways - ten tiles will not fit a 360px screen
 * at a readable size, and stacking them would push the rest of the page a
 * screen and a half down. The rail keeps its own wheel and keyboard scrolling;
 * `no-scrollbar` only hides the bar. From `md` the row becomes a grid and the
 * scrolling stops mattering: five across from `md`, and the reference's full
 * row of ten only at `xl`, where a tile is wide enough for "Beauty & Personal
 * Care" to sit on two lines rather than four.
 */

export function IndustriesRail() {
  const rail = useRef<HTMLUListElement>(null);
  const overflowing = useOverflowing(rail);

  return (
    <section
      aria-labelledby="home-industries"
      className="border-y border-line bg-brand-tint/60 py-6 sm:py-8"
    >
      <Container>
        <SectionHeading
          id="home-industries"
          heading={INDUSTRIES_SECTION.heading}
          subheading={INDUSTRIES_SECTION.subheading}
        />

        <ul
          ref={rail}
          aria-label={INDUSTRIES_SECTION.railLabel}
          tabIndex={overflowing ? 0 : undefined}
          className="no-scrollbar -mx-5 mt-6 flex gap-3 overflow-x-auto px-5 pb-1 sm:-mx-8 sm:px-8 md:mx-0 md:mt-8 md:grid md:grid-cols-5 md:overflow-visible md:px-0 xl:grid-cols-10"
        >
          {INDUSTRIES.map((industry) => (
            <li
              key={industry.label}
              className="w-28 shrink-0 overflow-hidden rounded-xl border border-line bg-white shadow-tile md:w-auto"
            >
              {/* The label right below says what this is, so the artwork is
                  decoration and takes an empty `alt` rather than repeating it.

                  These are background-removed cutouts, so they are shown whole
                  on a tinted panel - `contain`, not `cover`. Cropping a cutout
                  would slice the subject that was cut out in the first place,
                  and with nothing but transparency around it there is no
                  background for the crop to trim. Stored at 480x360, which
                  covers a 3x screen at the widest this tile ever gets. */}
              <img
                src={industry.image}
                alt=""
                className="aspect-[4/3] w-full bg-surface object-contain p-1.5"
                loading="lazy"
                decoding="async"
                width={480}
                height={360}
              />
              <span className="block px-2 py-2.5 text-center text-xs font-medium leading-snug text-ink-soft">
                {industry.label}
              </span>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}
